import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Claims() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/claims");
      setClaims(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  return (
    <div data-testid="claims-page">
      <PageHeader
        title="Report Claims"
        subtitle={`${claims.filter((c) => c.status === "open").length} offen`}
        actions={
          <Button
            onClick={() => setOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="claim-new-btn"
          >
            <Plus size={16} className="mr-2" /> Neuer Claim
          </Button>
        }
      >
        E-Mail-Dialoge mit dem Lightspeed Partner Consultant. Antworten landen automatisch im
        Thread, solange der Dialog «offen» ist.
      </PageHeader>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-6">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Betreff</th>
                <th className="text-left px-4 py-3 font-semibold">Empfänger</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Nachrichten</th>
                <th className="text-left px-4 py-3 font-semibold">Letzte Aktivität</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                    <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Lade Claims …
                  </td>
                </tr>
              )}
              {!loading && claims.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                    <MessageSquareWarning size={28} className="mx-auto text-slate-300 mb-2" />
                    Noch keine Claims. Eröffne den ersten Dialog aus einem Report-Detail.
                  </td>
                </tr>
              )}
              {claims.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`claim-row-${c.id}`}
                >
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/claims/${c.id}`}
                      className="hover:underline underline-offset-2"
                      data-testid={`claim-link-${c.id}`}
                    >
                      {c.subject}
                    </Link>
                    {c.related_report_po && (
                      <div className="text-xs text-slate-500 mt-0.5 font-mono">
                        Report {c.related_report_po}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{c.recipient_email}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{c.message_count}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDateTime(c.last_message_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <NewClaimDialog open={open} onOpenChange={setOpen} onCreated={reload} />
    </div>
  );
}

function NewClaimDialog({ open, onOpenChange, onCreated }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipient, setRecipient] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubject("");
      setBody("");
      setRecipient("");
    }
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/claims", {
        subject,
        body,
        recipient_email: recipient || undefined,
      });
      toast.success("Claim erstellt und E-Mail gesendet");
      onCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="claim-new-dialog">
        <DialogHeader>
          <DialogTitle>Neuen Claim eröffnen</DialogTitle>
          <DialogDescription>
            Wird via SMTP an den Empfänger gesendet. Wenn das Feld leer bleibt, wird die in den
            Einstellungen hinterlegte Standardadresse verwendet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="rcpt">Empfänger (optional)</Label>
            <Input
              id="rcpt"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="karim.kessab@lightspeedhq.com"
              data-testid="claim-recipient-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="csub">Betreff</Label>
            <Input
              id="csub"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="claim-subject-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cbody">Nachricht</Label>
            <Textarea
              id="cbody"
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="claim-body-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !subject.trim() || !body.trim()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="claim-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
